export const DEAL_CAME_FROM_SOURCES = [
  "LEAD_GENERATION",
  "IMPORT",
  "MANUAL",
  "REFERRAL",
] as const;

export type DealCameFromSource = (typeof DEAL_CAME_FROM_SOURCES)[number];
export type DealCameFromPlatform = "facebook" | "instagram";

const SOURCE_SET = new Set<string>(DEAL_CAME_FROM_SOURCES);
const SELLER_STAGES = new Set(["LEAD", "QUALIFYING"]);
const BUYER_STAGES = new Set(["NEW_INQUIRY"]);

export function isDealCameFromSource(value: string): value is DealCameFromSource {
  return SOURCE_SET.has(value);
}

export function dealCameFromChoice(source: DealCameFromSource): {
  label: string;
  hint: string;
} {
  switch (source) {
    case "LEAD_GENERATION":
      return {
        label: "Lead generation form",
        hint: "They filled out an ad form. This does not add them to a campaign.",
      };
    case "IMPORT":
      return {
        label: "Imported",
        hint: "Added from a file you imported.",
      };
    case "REFERRAL":
      return {
        label: "Referral",
        hint: "Someone referred them to you.",
      };
    case "MANUAL":
      return {
        label: "Added by you",
        hint: "Someone on your team added them.",
      };
  }
}

export function dealCameFromLockedReason(input: {
  pipelineType: string;
  stage: string;
  linkedToCampaign: boolean;
  hasIntroduction: boolean;
}): "campaign" | "introduction" | "stage" | null {
  if (input.linkedToCampaign) return "campaign";
  if (input.hasIntroduction) return "introduction";
  const open =
    input.pipelineType === "SELLER"
      ? SELLER_STAGES.has(input.stage)
      : input.pipelineType === "BUYER"
        ? BUYER_STAGES.has(input.stage)
        : false;
  return open ? null : "stage";
}

export function dealCameFromStageHint(
  pipelineType: string,
): string {
  return pipelineType === "BUYER"
    ? "You can change this while the inquiry is still new."
    : "You can change this while the lead is in New leads or Qualifying.";
}

export function readCameFromPlatform(
  metadata: unknown,
): DealCameFromPlatform | null {
  if (!metadata || typeof metadata !== "object" || Array.isArray(metadata)) {
    return null;
  }
  const cameFrom = (metadata as { cameFrom?: unknown }).cameFrom;
  if (!cameFrom || typeof cameFrom !== "object" || Array.isArray(cameFrom)) {
    return null;
  }
  const platform = (cameFrom as { platform?: unknown }).platform;
  return platform === "facebook" || platform === "instagram" ? platform : null;
}

export function mergeCameFromMetadata(
  metadata: unknown,
  source: DealCameFromSource,
  platform: DealCameFromPlatform | null,
): Record<string, unknown> {
  const record =
    metadata && typeof metadata === "object" && !Array.isArray(metadata)
      ? { ...(metadata as Record<string, unknown>) }
      : {};
  delete record.cameFrom;
  if (source === "LEAD_GENERATION" && platform) {
    record.cameFrom = { platform };
  }
  return record;
}
